import { useEffect, useState } from "react";
import api from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import DocumentUploader from "@/components/DocumentUploader";
import DocumentRow from "@/components/DocumentRow";
import { FolderOpen, Users } from "lucide-react";

export default function AdminDocuments() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => {
      setClients(r.data);
      if (r.data.length > 0) setSelectedId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selectedId) { setDocs([]); return; }
    setLoading(true);
    api.get(`/documents?client_id=${encodeURIComponent(selectedId)}`)
      .then((r) => setDocs(r.data))
      .finally(() => setLoading(false));
  }, [selectedId]);

  const onUploaded = (doc) => setDocs((prev) => [doc, ...prev]);
  const onDeleted = (id) => setDocs((prev) => prev.filter((d) => d.id !== id));

  const selected = clients.find((c) => c.id === selectedId);

  return (
    <DashboardLayout
      role="admin"
      title="Client Documents"
      subtitle="Securely share project files with each client. Files are private — only you and the assigned client can see them."
    >
      {clients.length === 0 ? (
        <div className="card p-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>
          <Users className="mx-auto mb-3" />
          No clients have signed up yet. Once a client registers via your <code>/register</code> page, you can share documents with them here.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-6">
          {/* Client list */}
          <div className="card p-3 h-fit max-h-[70vh] overflow-y-auto">
            <div className="text-xs uppercase tracking-wider px-2 py-2 font-semibold" style={{ color: 'var(--text-muted)' }}>
              Clients
            </div>
            <div className="space-y-1">
              {clients.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedId(c.id)}
                  className={`w-full text-left p-3 rounded-md transition-colors ${selectedId === c.id ? 'bg-white/5' : 'hover:bg-white/[0.03]'}`}
                  style={{ borderLeft: selectedId === c.id ? '2px solid var(--accent)' : '2px solid transparent' }}
                  data-testid={`admin-doc-client-${c.id}`}
                >
                  <div className="font-medium text-sm truncate">{c.name}</div>
                  <div className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{c.email}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Documents area */}
          <div className="space-y-5">
            {selected && (
              <div className="card p-5">
                <div className="text-xs uppercase tracking-wider mb-1 font-semibold" style={{ color: 'var(--text-muted)' }}>
                  Sharing with
                </div>
                <div className="font-display text-xl font-bold">{selected.name}</div>
                <div className="text-sm" style={{ color: 'var(--text-muted)' }}>{selected.email}</div>
              </div>
            )}

            <div className="card p-5">
              <div className="text-xs uppercase tracking-wider mb-3 font-semibold" style={{ color: 'var(--text-muted)' }}>
                Upload to this client's vault
              </div>
              <DocumentUploader clientId={selectedId} onUploaded={onUploaded} testid="admin-doc" />
            </div>

            <div className="card p-5">
              <div className="font-display text-lg font-bold mb-1">Files</div>
              <div className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
                {docs.length} {docs.length === 1 ? "file" : "files"} · only {selected?.name?.split(" ")[0] || "this client"} and admins can access
              </div>

              {loading ? (
                <div className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading…</div>
              ) : docs.length === 0 ? (
                <div className="p-10 text-center text-sm rounded-lg" style={{ color: 'var(--text-muted)', background: 'rgba(11,19,43,0.4)' }}>
                  <FolderOpen className="mx-auto mb-3" />
                  Nothing here yet. Upload contracts, plans, permits, or photos above.
                </div>
              ) : (
                <div className="space-y-2.5" data-testid="admin-doc-list">
                  {docs.map((d) => (
                    <DocumentRow key={d.id} doc={d} canDelete onDeleted={onDeleted} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
